import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toISODate } from '../utils/date';
import {
  ClipboardDocumentListIcon,
  UserGroupIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { childrenApi } from '../services/api/children';
import { Child } from '../types/api';
import { observationsApi } from '../services/api/observations';
import { useLearningAreas } from '../hooks/api/useAssessments';
import { extractApiErrorMessage } from '../utils/apiError';
import {
  H1,
  H2,
  Text,
  Card,
  Button,
  Input,
  Textarea,
  Select,
  Badge,
  Spinner,
  Checkbox,
  useToast,
} from '../components/ui';

const bulkObservationSchema = z.object({
  observation_date: z.string().min(1, 'Date is required'),
  learning_area_id: z.string().min(1, 'Please select a learning area'),
  category: z.enum(['general', 'academic', 'social', 'emotional', 'physical']),
  narrative: z.string().min(10, 'Observation must be at least 10 characters'),
  is_milestone: z.boolean().default(false),
  is_concern: z.boolean().default(false),
  follow_up_required: z.boolean().default(false),
  is_private: z.boolean().default(false),
  tags: z.string().optional(),
});

type BulkObservationFormData = z.infer<typeof bulkObservationSchema>;

interface SelectedChild {
  id: string;
  name: string;
  surname: string;
}

const BulkObservations: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { warning, success, error: showError } = useToast();
  const [selectedChildren, setSelectedChildren] = useState<SelectedChild[]>([]);
  const [expandedClassrooms, setExpandedClassrooms] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<BulkObservationFormData>({
    resolver: zodResolver(bulkObservationSchema),
    defaultValues: {
      observation_date: toISODate(),
      learning_area_id: '',
      category: 'general',
      is_milestone: false,
      is_concern: false,
      follow_up_required: false,
      is_private: false,
    },
  });

  // Fetch all active children
  const { data: children, isLoading: childrenLoading } = useQuery({
    queryKey: ['children', 'active'],
    queryFn: () => childrenApi.getChildren({ is_active: true }),
  });

  // Fetch learning areas for the dropdown
  const { data: learningAreas } = useLearningAreas();

  // Group children by classroom (falls back to "All Children" when no classroom assigned)
  const childrenByClassroom = React.useMemo(() => {
    if (!children?.items) return {} as Record<string, Child[]>;

    const grouped: Record<string, Child[]> = {};
    children.items.forEach((child) => {
      const groupName = child.classroom?.name || 'Unassigned';
      if (!grouped[groupName]) {
        grouped[groupName] = [];
      }
      grouped[groupName].push(child);
    });

    return grouped;
  }, [children]);

  // Filter children based on search term
  const filteredChildrenByClassroom = React.useMemo(() => {
    if (!searchTerm) return childrenByClassroom;

    const filtered: Record<string, Child[]> = {};
    Object.entries(childrenByClassroom).forEach(([classroom, classChildren]) => {
      const matches = classChildren.filter((child) => {
        const fullName = `${child.name} ${child.surname}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase());
      });

      if (matches.length > 0) {
        filtered[classroom] = matches;
      }
    });

    return filtered;
  }, [childrenByClassroom, searchTerm]);

  // Bulk observation mutation — posts a single request with shared observation data
  // and the list of selected child IDs per the API bulk contract.
  const bulkObservationMutation = useMutation({
    mutationFn: async (data: BulkObservationFormData) => {
      const tags = data.tags
        ? data.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : undefined;

      // NOTE: The backend `ObservationCreate` schema requires `child_id` for
      // pydantic validation, even though the bulk handler overrides it per
      // child (see sprout/api/routes/observations.py:349). We pass the first
      // selected child's ID as a placeholder to satisfy the schema.
      return observationsApi.bulkCreate({
        child_ids: selectedChildren.map((child) => child.id),
        observation_data: {
          child_id: selectedChildren[0].id,
          observation_date: data.observation_date,
          category: data.category,
          learning_area_id: data.learning_area_id,
          narrative: data.narrative,
          is_milestone: data.is_milestone,
          is_concern: data.is_concern,
          follow_up_required: data.follow_up_required,
          is_private: data.is_private,
          tags,
        },
        auto_verify: true,
      });
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['observations'] });

      const createdCount = response.created.length;
      const failedCount = response.failed.length;

      if (createdCount > 0) {
        success(`Created ${createdCount} observation${createdCount === 1 ? '' : 's'}`);
      }
      if (failedCount > 0) {
        showError(
          `${failedCount} observation${failedCount === 1 ? '' : 's'} failed`,
          response.failed[0]?.error,
        );
      }

      // Reset form and selection
      reset();
      setSelectedChildren([]);

      if (createdCount > 0) {
        navigate('/observations');
      }
    },
    onError: (err: unknown) => {
      showError(extractApiErrorMessage(err, 'Failed to create observations'));
    },
  });

  const toggleClassroom = (classroom: string) => {
    const newExpanded = new Set(expandedClassrooms);
    if (newExpanded.has(classroom)) {
      newExpanded.delete(classroom);
    } else {
      newExpanded.add(classroom);
    }
    setExpandedClassrooms(newExpanded);
  };

  const toggleChild = (child: Child) => {
    const isSelected = selectedChildren.some((c) => c.id === child.id);

    if (isSelected) {
      setSelectedChildren(selectedChildren.filter((c) => c.id !== child.id));
    } else {
      setSelectedChildren([
        ...selectedChildren,
        {
          id: child.id,
          name: child.name,
          surname: child.surname,
        },
      ]);
    }
  };

  const selectAllInClassroom = (classroom: string) => {
    const classroomChildren = filteredChildrenByClassroom[classroom] || [];
    const allSelected = classroomChildren.every((child) =>
      selectedChildren.some((c) => c.id === child.id)
    );

    if (allSelected) {
      setSelectedChildren(
        selectedChildren.filter(
          (c) => !classroomChildren.some((child) => child.id === c.id)
        )
      );
    } else {
      const newSelections = classroomChildren
        .filter((child) => !selectedChildren.some((c) => c.id === child.id))
        .map((child) => ({
          id: child.id,
          name: child.name,
          surname: child.surname,
        }));

      setSelectedChildren([...selectedChildren, ...newSelections]);
    }
  };

  const onSubmit = (data: BulkObservationFormData) => {
    if (selectedChildren.length === 0) {
      warning('Please select at least one child');
      return;
    }

    bulkObservationMutation.mutate(data);
  };

  if (childrenLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-state">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 lg:pb-0">
      <div>
        <H1>Bulk Observations</H1>
        <Text className="mt-2 text-gray-600 dark:text-gray-300">
          Create the same observation for multiple children at once
        </Text>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Child Selection */}
        <Card className="p-6">
          <H2 className="mb-4 flex items-center">
            <UserGroupIcon className="w-5 h-5 mr-2" />
            Select Children ({selectedChildren.length} selected)
          </H2>

          {/* Search */}
          <div className="mb-4">
            <Input
              type="text"
              placeholder="Search children..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Classroom Groups */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {Object.entries(filteredChildrenByClassroom).map(([classroom, classroomChildren]) => {
              const isExpanded = expandedClassrooms.has(classroom);
              const allSelected =
                classroomChildren.length > 0 &&
                classroomChildren.every((child) =>
                  selectedChildren.some((c) => c.id === child.id)
                );

              return (
                <div
                  key={classroom}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  <div
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 cursor-pointer"
                    onClick={() => toggleClassroom(classroom)}
                  >
                    <div className="flex items-center">
                      {isExpanded ? (
                        <ChevronUpIcon className="w-4 h-4 mr-2" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 mr-2" />
                      )}
                      <Text className="font-medium">{classroom}</Text>
                      <Badge variant="secondary" size="sm" className="ml-2">
                        {classroomChildren.length}
                      </Badge>
                    </div>
                    <div
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`select-all-${classroom}`}
                    >
                      <Checkbox
                        checked={allSelected}
                        onChange={() => selectAllInClassroom(classroom)}
                      />
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="p-3 space-y-2">
                      {classroomChildren.map((child) => {
                        const isSelected = selectedChildren.some((c) => c.id === child.id);

                        return (
                          <label
                            key={child.id}
                            className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer"
                          >
                            <div className="flex items-center">
                              <Checkbox
                                checked={isSelected}
                                onChange={() => toggleChild(child)}
                              />
                              <Text className="ml-3">
                                {child.name} {child.surname}
                              </Text>
                            </div>
                            {child.age !== undefined && (
                              <Text className="text-sm text-gray-500 dark:text-gray-400">
                                Age {child.age}
                              </Text>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Selected Children Summary */}
          {selectedChildren.length > 0 && (
            <div className="mt-4 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <Text className="text-sm font-medium text-primary-900 dark:text-primary-100 mb-2">
                Selected Children:
              </Text>
              <div className="flex flex-wrap gap-1">
                {selectedChildren.map((child) => (
                  <Badge key={child.id} variant="primary" size="sm">
                    {child.name} {child.surname}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Observation Form */}
        <Card className="p-6">
          <H2 className="mb-4 flex items-center">
            <ClipboardDocumentListIcon className="w-5 h-5 mr-2" />
            Observation Details
          </H2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date
              </label>
              <Input
                type="date"
                {...register('observation_date')}
                error={errors.observation_date?.message}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category
              </label>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Select {...field} error={errors.category?.message}>
                    <option value="general">General</option>
                    <option value="academic">Academic</option>
                    <option value="social">Social</option>
                    <option value="emotional">Emotional</option>
                    <option value="physical">Physical</option>
                  </Select>
                )}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Learning Area <span className="text-red-500">*</span>
              </label>
              <Controller
                name="learning_area_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    error={errors.learning_area_id?.message}
                    options={[
                      { value: '', label: 'Select a learning area...' },
                      ...(learningAreas ?? []).map((la) => ({
                        value: la.id,
                        label: `${la.code} — ${la.name}`,
                      })),
                    ]}
                  />
                )}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Observation
              </label>
              <Textarea
                {...register('narrative')}
                rows={6}
                placeholder="Describe what you observed..."
                error={errors.narrative?.message}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tags (Optional)
              </label>
              <Input
                {...register('tags')}
                placeholder="e.g., teamwork, creativity (comma-separated)"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center">
                <Controller
                  name="is_milestone"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={!!field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  This is a milestone
                </span>
              </label>

              <label className="flex items-center">
                <Controller
                  name="is_concern"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={!!field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  This is a concern
                </span>
              </label>

              <label className="flex items-center">
                <Controller
                  name="follow_up_required"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={!!field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Follow-up required
                </span>
              </label>

              <label className="flex items-center">
                <Controller
                  name="is_private"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={!!field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Keep private (don't share with parents)
                </span>
              </label>
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                isLoading={isSubmitting || bulkObservationMutation.isPending}
                disabled={
                  isSubmitting ||
                  bulkObservationMutation.isPending ||
                  selectedChildren.length === 0
                }
                className="flex-1"
              >
                Create {selectedChildren.length} Observation
                {selectedChildren.length === 1 ? '' : 's'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/observations')}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default BulkObservations;
